<!--
  ~ Copyright (C) 2026 - Potentially Ltd
  ~
  ~ Please see distribution for license.
  -->

@if (organizationData$ | async; as organizationData) {
  <main
    class="v3-main"
    [ngStyle]="{
      'background-image': organizationData.landingPage.backgroundImage
        ? 'url(&quot;' + organizationData.landingPage.backgroundImage + '&quot;)'
        : '',
      'background-color': organizationData.landingPage.useBackgroundColourFill ? organizationData.landingPage.backgroundColour : '',
    }"
    [class.dynamic-background-color]="organizationData.landingPage.useBackgroundColourFill"
    [class.split-screen]="organizationData.landingPage.layout === 'SPLIT_SCREEN'"
  >
    @if (showMaintenanceOverlay) {
      <div class="maintenance-layer">
        <div class="maintenance-layer-inner" [innerHTML]="'translations.maintenance.overlayText' | transloco"></div>
      </div>
    }

    <div class="v3-form-container" [class.split-screen]="organizationData.landingPage.layout === 'SPLIT_SCREEN'">
      <div class="v3-login-card" [class.split-screen]="organizationData.landingPage.layout === 'SPLIT_SCREEN'">
        <div class="v3-brand-row">
          <img
            class="v3-brand-logo"
            width="44"
            height="44"
            [src]="organizationData.landingPage.landingLogo || organizationData.logo"
            alt="{{ organizationData.name }} Logo"
          />
          <span class="v3-brand-name semibold">{{ organizationData.name }}</span>
        </div>

        <div class="v3-headline">
          @if (!userPreviouslyLoggedIn) {
            <h1 class="v3-title">{{ organizationData.name }}</h1>
          } @else {
            <h1 class="v3-title">{{ 'translations.public.title.welcome' | transloco }}</h1>
          }
          <p class="v3-subtitle fs-body-m">{{ organizationData.landingPage.welcomeMessage }}</p>
        </div>

        <div
          class="v3-login-form"
          [formGroup]="authenticationDetails"
          role="form"
          [attr.aria-label]="'translations.global.ariaLabels.signInForm' | transloco"
        >
          @let isLoggingIn = loginInProgress$ | async;

          <ptl-sg-input
            class="v3-field"
            type="email"
            formControlName="email"
            [label]="'translations.global.input.label.email' | transloco"
            (keydown.enter)="submitForm()"
          ></ptl-sg-input>
          @if (authenticationDetails.controls.email.touched && authenticationDetails.controls.email.hasError('required')) {
            <p class="v3-field-error fs-body-xs-alt">{{ 'translations.global.input.error.enterEmail' | transloco }}</p>
          }

          <div class="v3-password-field">
            <ptl-sg-input
              class="v3-field"
              [type]="showPasswordSelected ? 'text' : 'password'"
              formControlName="password"
              [label]="'translations.global.input.label.password' | transloco"
              (keydown.enter)="submitForm()"
            ></ptl-sg-input>
            <button
              type="button"
              class="v3-password-toggle"
              [attr.aria-label]="'translations.global.ariaLabels.showOrHidePassword' | transloco"
              (click)="showPasswordSelected = !showPasswordSelected"
            >
              <ptl-icon [icon]="showPasswordSelected ? LucideEyeOff : LucideEye" size="xs"></ptl-icon>
            </button>
          </div>
          @if (authenticationDetails.controls.password.touched && authenticationDetails.controls.password.hasError('required')) {
            <p class="v3-field-error fs-body-xs-alt">{{ 'translations.global.input.error.enterPassword' | transloco }}</p>
          }

          <div class="v3-actions">
            <ptl-sg-button type="flat" color="primary" class="v3-submit" [disabled]="isLoggingIn" (clicked)="submitForm()">
              @if (!isLoggingIn) {
                <span>{{ 'translations.public.button.login' | transloco }}</span>
              } @else {
                <span class="v3-submit-loading">
                  <span>{{ 'translations.public.button.loggingIn' | transloco }}</span>
                  <mat-spinner [diameter]="18"></mat-spinner>
                </span>
              }
            </ptl-sg-button>

            <div class="v3-actions-bottom">
              <button type="button" class="v3-link-action" (click)="onForgotPassword()">
                {{ 'translations.public.button.forgotPassword' | transloco }}
              </button>
              @if (organizationData.allowRegistrations) {
                <button type="button" class="v3-link-action" (click)="onNotMemberClick()">
                  {{ 'translations.public.button.signUp' | transloco }}
                </button>
              }
            </div>
          </div>

          @if (isSocialAuthType) {
            <div class="v3-divider">
              <span class="overline" [attr.aria-label]="'translations.global.ariaLabels.orSignUpWith' | transloco">{{
                'translations.public.label.signInWith' | transloco
              }}</span>
            </div>
            <div class="v3-social-buttons">
              <button class="v3-social-button v3-social-button--linkedin" title="Sign in with LinkedIn" (click)="signInUsingLinkedIn()">
                <img alt="" src="assets/social/linkedin-white.svg" width="291" height="79" />
              </button>
              <button class="v3-social-button v3-social-button--google" title="Sign in with Google" (click)="signInUsingGoogle()">
                <img alt="" src="assets/social/google-colored.svg" width="265" height="87" />
              </button>
            </div>
          }
        </div>
      </div>
    </div>
  </main>
} @else {
  <div class="v3-loading-container">
    <div class="v3-loading-logo">
      <img src="https://static.yuna.potential.ly/public/images/icons/potentially_logo.png" alt="Potential.ly Logo" width="60" height="61" />
      <span class="section-title">{{ 'translations.public.lti.title.logoText' | transloco }}</span>
    </div>
    <div class="v3-loading-in-progress">
      <span class="fs-body-m">{{ 'translations.loginFormLoading.loading' | transloco }}</span>
      <mat-spinner [diameter]="25"></mat-spinner>
    </div>
    <div class="v3-loading-warning fs-body-s bold">{{ 'translations.loginFormLoading.loadingTooLongWarning' | transloco }}</div>
  </div>
}
